import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { TOP_NAV_ANCHORS } from "@/lib/sections";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { Logo } from "@/components/brand/Logo";
import { cn } from "@/lib/cn";

/**
 * Top navigation for the single-page paper. Four curated anchor jumps that
 * span the three acts, plus theme toggle. The logo returns to the top
 * (#hero). Presentation Mode is intentionally not surfaced here.
 */
export const TopNav = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const jump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // update hash without jump
      window.history.replaceState(null, "", `#${id}`);
    }
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="top-nav"
      className="sticky top-0 z-40 w-full border-b border-border bg-background/80 backdrop-blur-md"
    >
      <div className="haevn-container flex h-14 items-center justify-between gap-6">
        <div className="flex items-center gap-8">
          <a
            href="#hero"
            onClick={jump("hero")}
            data-testid="nav-home-link"
            aria-label="HAEVN — return to top"
          >
            <Logo />
          </a>
          <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
            {TOP_NAV_ANCHORS.map((a) => (
              <a
                key={a.id}
                href={`#${a.id}`}
                onClick={jump(a.id)}
                data-testid={`nav-link-${a.id}`}
                className={cn(
                  "rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors",
                  "text-muted-foreground hover:text-foreground"
                )}
              >
                {a.label}
              </a>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button
            type="button"
            data-testid="mobile-nav-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:bg-surface hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background md:hidden"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-nav-drawer"
          className="md:hidden border-t border-border bg-background animate-fade-in"
        >
          <nav aria-label="Primary mobile" className="haevn-container flex flex-col py-4">
            {TOP_NAV_ANCHORS.map((a) => (
              <a
                key={a.id}
                href={`#${a.id}`}
                onClick={jump(a.id)}
                data-testid={`mobile-nav-link-${a.id}`}
                className="rounded-md px-2 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-surface/60"
              >
                {a.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default TopNav;
